import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { didCancel, task } from 'ember-concurrency';
import type {
  BulkApplyBody,
  BulkApplyFilters,
  BulkUserUpdate,
  default as SubscriptionUserAdapter,
} from 'lumen/adapters/subscription-user';
import type { SubscriptionOption } from 'lumen/components/settings/users/subscription-select';
import type {
  BulkApplyChange,
  BulkDeleteChange,
  UserChange,
} from 'lumen/components/settings/users/tables/users-table';
import type ManagedUser from 'lumen/models/managed-user';
import type SubscriptionUser from 'lumen/models/subscription-user';
import type Userhotelrelationship from 'lumen/models/userhotelrelationship';
import type {
  RouteParams,
  UsersModel,
} from 'lumen/routes/hotel/settings/users';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { ACCESS_RIGHTS, PRODUCTS } from 'lumen/utils/constants/users-constants';
import { timeout } from 'lumen/utils/waiters';

type SortableColumn = 'name' | 'role';

export interface InvitePayload {
  accessRight: string;
  copyReports: boolean;
  emails: string[];
  products: string[];
  subscriptionId: string;
}

export default class HotelSettingsUsersController extends Controller {
  declare model: UsersModel;

  queryParams = [
    'sortBy',
    'sortDirection',
    { search: 'q' },
    'role',
    'product',
    'subscription',
  ];

  @service declare access: Services['access'];
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare store: Services['store'];
  @service declare toast: Services['toast'];

  @tracked accumulatedManagedUsers: ManagedUser[] = [];
  @tracked accumulatedUsers: SubscriptionUser[] = [];
  @tracked currentPage = 1;
  @tracked isInviteModalVisible = false;
  @tracked product: string | null = null;
  @tracked role: string | null = null;
  @tracked search: string | null = null;
  @tracked searchInputValue: string | null = null;
  @tracked selectedSubscriptionOption: SubscriptionOption | null = null;
  @tracked sortBy: SortableColumn = 'name';
  @tracked sortDirection: 'asc' | 'desc' = 'asc';
  @tracked subscription: string | null = null;
  @tracked totalRecords = 0;

  debouncedSearchTask = task({ restartable: true }, async (value: string) => {
    await timeout();

    const trimmed = value.trim();
    if (!trimmed) {
      this.search = null;
    } else {
      this.search = trimmed
        .split(',')
        .map((name) => name.trim())
        .filter(Boolean)
        .join(',');
    }
  });

  loadMoreTask = task({ drop: true }, async (): Promise<void> => {
    if (
      !this.hasMore ||
      this.model.loadUsersTask.isRunning ||
      this.model.loadManagedUsersTask.isRunning
    ) {
      return;
    }

    const params = this.buildLoadParams(this.currentPage + 1);

    if (this.isAllSubscriptions) {
      const result = await this.model.loadManagedUsersTask.perform(params);

      if (result) {
        this.accumulatedManagedUsers = [
          ...this.accumulatedManagedUsers,
          ...result.users,
        ];
        this.totalRecords = result.meta.total_items;
        this.currentPage = this.currentPage + 1;
      } else {
        this.totalRecords = this.accumulatedManagedUsers.length;
      }
      return;
    }

    const result = await this.model.loadUsersTask.perform(params);

    if (result) {
      this.accumulatedUsers = [...this.accumulatedUsers, ...result.users];
      this.totalRecords = result.meta.total_items;
      this.currentPage = this.currentPage + 1;
    } else {
      this.totalRecords = this.accumulatedUsers.length;
    }
  });

  private get bulkFilters(): BulkApplyFilters {
    const filters: BulkApplyFilters = {};

    if (this.role) {
      filters.role = this.role;
    }
    if (this.product) {
      filters.product = this.product;
    }
    if (this.search) {
      filters.q = this.search;
    }

    return filters;
  }

  get displaySearchValue(): string {
    if (this.searchInputValue !== null) {
      return this.searchInputValue;
    }

    return this.search ?? '';
  }

  get hasMore(): boolean {
    const loadedCount = this.isAllSubscriptions
      ? this.accumulatedManagedUsers.length
      : this.accumulatedUsers.length;
    return loadedCount < this.totalRecords;
  }

  get isAdmin(): boolean {
    if (this.access.isStaffUser) {
      return true;
    }

    const user = this.hotelAndMarketService.user;

    if (!this.subscription) {
      return false;
    }

    const relationships =
      user.userhotelrelationships as unknown as Userhotelrelationship[];
    const relationship = relationships.find(
      (rel) => rel.subscriptionId === Number(this.subscription),
    );

    return relationship?.accessRight === ACCESS_RIGHTS.ADMIN;
  }

  get isAllSubscriptions(): boolean {
    return this.subscription === null;
  }

  get isLoading(): boolean {
    // Pagination reports through isLoadingMore: swapping the table body for the
    // loading skeleton collapses the scroll container and jumps back to the top.
    if (this.isLoadingMore) {
      return false;
    }

    return (
      (this.isAllSubscriptions
        ? this.model.loadManagedUsersTask.isRunning
        : this.model.loadUsersTask.isRunning) ?? false
    );
  }

  get isLoadingMore(): boolean {
    return this.loadMoreTask.isRunning;
  }

  get selectedSubscription(): SubscriptionOption | null {
    return this.selectedSubscriptionOption;
  }

  get sorts(): { isAscending: boolean; valuePath: SortableColumn }[] {
    return [
      {
        // The managed-users endpoint only sorts on `name`, so a `role` sort
        // carried over from the subscription view must not render as active.
        valuePath: this.isAllSubscriptions ? 'name' : this.sortBy,
        isAscending: this.sortDirection === 'asc',
      },
    ];
  }

  buildLoadParams(page: number): RouteParams {
    return {
      page,
      sortBy: this.sortBy,
      sortDirection: this.sortDirection,
      search: this.search ?? undefined,
      role: this.isAllSubscriptions ? undefined : (this.role ?? undefined),
      product: this.isAllSubscriptions
        ? undefined
        : (this.product ?? undefined),
      subscription: this.subscription ?? undefined,
    };
  }

  async resetAndLoad(): Promise<void> {
    this.accumulatedManagedUsers = [];
    this.accumulatedUsers = [];
    this.currentPage = 1;
    this.totalRecords = 0;
    this.searchInputValue = this.search;

    const params = this.buildLoadParams(1);

    try {
      if (this.subscription && !this.selectedSubscriptionOption) {
        this.selectedSubscriptionOption =
          (await this.model.loadSubscriptionOptionTask.perform(
            this.subscription,
          )) ?? null;
      }

      if (this.isAllSubscriptions) {
        const result = await this.model.loadManagedUsersTask.perform(params);

        if (result) {
          this.accumulatedManagedUsers = result.users;
          this.totalRecords = result.meta.total_items;
        }
        return;
      }

      const result = await this.model.loadUsersTask.perform(params);

      if (result) {
        this.accumulatedUsers = result.users;
        this.totalRecords = result.meta.total_items;
      }
    } catch (error) {
      // Ignore ember concurrency task cancellation errors because the load tasks are restartable and get canceled when a new load starts or the route is torn down.
      if (!didCancel(error)) {
        // But re-throw the non-cancelation error
        throw error;
      }
    }
  }

  @action
  async applyBulkChanges(payload: BulkApplyChange): Promise<boolean> {
    if (!this.subscription) {
      return false;
    }

    const confirmed = await this.alerting.showConfirmWarningAlert({
      title: this.intl.t('settings.users.bulk_edit.confirm_title'),
      text: this.intl.t('settings.users.bulk_edit.confirm_text', {
        count: payload.selectedCount,
      }),
      unsafe_outsideClickCanCloseAlert: false,
    });

    if (!confirmed) {
      return false;
    }

    try {
      const body: BulkApplyBody = {};
      if (payload.role !== undefined) {
        body.role = payload.role;
      }
      if (payload.addProducts !== undefined) {
        body.add_products = payload.addProducts;
      }
      if (payload.removeProducts !== undefined) {
        body.remove_products = payload.removeProducts;
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      const adapter = this.store.adapterFor(
        'subscription-user',
      ) as SubscriptionUserAdapter;
      await adapter.bulkApply(this.subscription, body, {
        excludedUserIds: payload.excludedUserIds,
        filters: this.bulkFilters,
      });

      this.toast.success(
        this.intl.t('settings.users.edit.success', {
          count: payload.selectedCount,
        }),
        { showIcon: true },
      );

      await this.resetAndLoad();
      return true;
    } catch (error) {
      this.errorTracking.captureException(error);
      this.toast.error(
        this.intl.t('settings.users.edit.failure', {
          count: payload.selectedCount,
        }),
        { showIcon: true },
      );
      return false;
    }
  }

  @action
  async bulkDeleteUsers(payload: BulkDeleteChange): Promise<boolean> {
    const { excludedUserIds, isSelectAllActive, selectedCount, userIds } =
      payload;

    if (selectedCount === 0 || !this.subscription) {
      return false;
    }

    const hotelName =
      this.hotelAndMarketService.hotelOrMarket?.hotelinfo?.name ?? '';

    const confirmWasClicked = await this.alerting.showConfirmWarningAlert({
      dangerMode: true,
      title: this.intl.t('settings.users.bulk_delete.confirm_title', {
        count: selectedCount,
      }),
      text: this.intl.t('settings.users.bulk_delete.confirm_message', {
        count: selectedCount,
        hotelName,
      }),
      buttons: {
        cancel: {
          text: this.intl.t('general.cancel'),
        },
        confirm: {
          text: this.intl.t('general.confirm'),
        },
      },
    });

    if (!confirmWasClicked) {
      return false;
    }

    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      const adapter = this.store.adapterFor(
        'subscription-user',
      ) as SubscriptionUserAdapter;

      if (isSelectAllActive) {
        await adapter.bulkDeleteAll(this.subscription, {
          excludedUserIds,
          filters: this.bulkFilters,
        });
      } else {
        await adapter.deleteUsers(this.subscription, userIds);
      }

      this.toast.success(
        this.intl.t('settings.users.bulk_delete.success', {
          count: selectedCount,
        }),
        { showIcon: true },
      );

      await this.resetAndLoad();
      return true;
    } catch (error: unknown) {
      this.errorTracking.captureException(error);
      this.toast.error(
        this.intl.t('settings.users.bulk_delete.failure', {
          count: selectedCount,
        }),
        { showIcon: true },
      );
      return false;
    }
  }

  @action
  closeInviteModal(): void {
    this.isInviteModalVisible = false;
  }

  @action
  async deleteUser(user: SubscriptionUser): Promise<void> {
    const subscriptionId = this.subscription;

    if (!subscriptionId) {
      return;
    }

    const confirmWasClicked = await this.alerting.showConfirmWarningAlert({
      dangerMode: true,
      title: this.intl.t('settings.users.delete.confirm_title'),
      text: this.intl.t('settings.users.delete.confirm_message', {
        hotelName: this.selectedSubscriptionOption?.label ?? '',
      }),
      buttons: {
        cancel: {
          text: this.intl.t('general.cancel'),
        },
        confirm: {
          text: this.intl.t('general.confirm'),
        },
      },
    });

    if (!confirmWasClicked) {
      return;
    }

    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      const adapter = this.store.adapterFor(
        'subscription-user',
      ) as SubscriptionUserAdapter;
      await adapter.deleteUsers(subscriptionId, [Number(user.id)]);

      this.toast.success(this.intl.t('settings.users.delete.success'), {
        showIcon: true,
      });

      await this.resetAndLoad();
    } catch (error: unknown) {
      this.errorTracking.captureException(error);
      this.toast.error(this.intl.t('settings.users.delete.failure'), {
        showIcon: true,
      });
    }
  }

  @action
  async inviteUsers(payload: InvitePayload): Promise<boolean> {
    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      const adapter = this.store.adapterFor(
        'subscription-user',
      ) as SubscriptionUserAdapter;
      await adapter.invite(payload.subscriptionId, {
        access_right: payload.accessRight,
        accessible_products: payload.products,
        copy_reports: payload.copyReports,
        emails: payload.emails,
      });

      this.toast.success(this.intl.t('settings.users.invite.success'), {
        showIcon: true,
      });

      await this.resetAndLoad();
      return true;
    } catch (error) {
      this.errorTracking.captureException(error);
      this.toast.error(this.intl.t('settings.users.invite.failure'), {
        showIcon: true,
      });
      return false;
    }
  }

  @action
  onUpdateSorts(sorts: { isAscending: boolean; valuePath: string }[]): void {
    if (sorts[0]) {
      this.sortBy = sorts[0].valuePath as SortableColumn;
      this.sortDirection = sorts[0].isAscending ? 'asc' : 'desc';
    } else {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    }
  }

  @action
  openInviteModal(): void {
    this.isInviteModalVisible = true;
  }

  @action
  async saveChanges(changes: UserChange[]): Promise<boolean> {
    if (!this.subscription) {
      return false;
    }

    const confirmed = await this.alerting.showConfirmWarningAlert({
      title: this.intl.t('settings.users.bulk_edit.confirm_title'),
      text: this.intl.t('settings.users.bulk_edit.confirm_text', {
        count: changes.length,
      }),
      buttons: {
        cancel: {
          text: this.intl.t('general.cancel'),
        },
        confirm: {
          text: this.intl.t('general.confirm'),
        },
      },
      unsafe_outsideClickCanCloseAlert: false,
    });

    if (!confirmed) {
      return false;
    }

    try {
      const users = changes.map((change) => {
        const update: BulkUserUpdate = { user_id: Number(change.userId) };

        if (change.newRole !== change.originalRole) {
          update.role = change.newRole;
        }

        const hasProductChanged =
          change.newRateAndMarketInsight !==
            change.originalRateAndMarketInsight ||
          change.newBiBenchmarking !== change.originalBiBenchmarking;

        if (hasProductChanged) {
          const products: string[] = [];
          if (change.newRateAndMarketInsight) {
            products.push(PRODUCTS.RATE_INSIGHT);
          }
          if (change.newBiBenchmarking) {
            products.push(PRODUCTS.BI_BENCHMARKING);
          }
          update.products = products;
        }

        return update;
      });

      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      const adapter = this.store.adapterFor(
        'subscription-user',
      ) as SubscriptionUserAdapter;
      await adapter.bulkUpdate(this.subscription, users);

      this.toast.success(
        this.intl.t('settings.users.edit.success', { count: changes.length }),
        { showIcon: true },
      );

      await this.resetAndLoad();
      return true;
    } catch (error) {
      this.errorTracking.captureException(error);
      this.toast.error(
        this.intl.t('settings.users.edit.failure', { count: changes.length }),
        { showIcon: true },
      );
      return false;
    }
  }

  @action
  updateProduct(product: string | null): void {
    this.product = product;
  }

  @action
  updateRole(role: string | null): void {
    this.role = role;
    this.accumulatedManagedUsers = [];
    this.accumulatedUsers = [];
    this.currentPage = 1;
    this.totalRecords = 0;
  }

  @action
  updateSearch(event: Event): void {
    this.searchInputValue = (event.target as HTMLInputElement).value;
    void this.debouncedSearchTask.perform(this.searchInputValue);
  }

  @action
  updateSubscription(selectedOption: SubscriptionOption | null): void {
    this.subscription = selectedOption?.id ?? null;
    this.selectedSubscriptionOption = selectedOption;

    if (this.isAllSubscriptions) {
      this.role = null;
      this.product = null;
    }
  }
}
